import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { IS_NATIVE } from '@/lib/nativeBridge';
import {
  fetchTeamTransferHistory,
  type BETeamTransferHistoryGameweek,
  type TeamTransferHistoryResponse,
} from '@/lib/network';

export const TEAM_TRANSFER_HISTORY_QUERY_KEY_PREFIX = ['teams', 'transfer-history'] as const;
const TEAM_TRANSFER_HISTORY_STALE_TIME_MS = 30 * 1000;
const TEAM_TRANSFER_HISTORY_GC_TIME_MS = 5 * 60 * 1000;

export const getTeamTransferHistoryQueryKey = (teamId: string | undefined) =>
  [...TEAM_TRANSFER_HISTORY_QUERY_KEY_PREFIX, teamId ?? ''] as const;

export type UseTeamTransferHistoryOptions = Omit<
  UseQueryOptions<TeamTransferHistoryResponse, Error, BETeamTransferHistoryGameweek[]>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useTeamTransferHistory = (
  teamId: string | undefined,
  options?: UseTeamTransferHistoryOptions
): UseQueryResult<BETeamTransferHistoryGameweek[], Error> => {
  const { enabled = true, ...restOptions } = options ?? {};

  return useQuery<TeamTransferHistoryResponse, Error, BETeamTransferHistoryGameweek[]>({
    queryKey: getTeamTransferHistoryQueryKey(teamId),
    queryFn: async () => {
      if (!teamId) {
        throw new Error('teamId is required to fetch team transfer history');
      }

      return fetchTeamTransferHistory(teamId);
    },
    select: response => response.data.items,
    enabled: enabled && !!teamId,
    staleTime: TEAM_TRANSFER_HISTORY_STALE_TIME_MS,
    gcTime: TEAM_TRANSFER_HISTORY_GC_TIME_MS,
    // The app webview stays open in the background, so refetch stale data when it becomes visible again.
    refetchOnWindowFocus: IS_NATIVE,
    ...restOptions,
  });
};
